import { useEffect, useRef, useState } from 'react';
import { GameSession } from '@/types';

const MS_IN_SECOND = 1000;

function ensurePositiveSeconds(value: number): number {
  if (!Number.isFinite(value)) {
    return 0;
  }
  return Math.max(0, Math.ceil(value));
}

export function useSessionPhaseTimer(
  session: GameSession | null,
  fallbackSeconds: number,
  options?: { resetKey?: unknown }
): number {
  const [nowMs, setNowMs] = useState<number>(() => Date.now());
  const localEndTimeMsRef = useRef<number | null>(null);
  const lastFallbackSecondsRef = useRef<number>(fallbackSeconds);
  const lastResetKeyRef = useRef<unknown>(options?.resetKey);

  // Extract primitive values from session to avoid stale closure issues
  // This ensures the effect re-runs when actual timestamp values change,
  // not just when the session object reference changes
  const endTimeMs = session?.currentPhaseEndTime instanceof Date
    ? session.currentPhaseEndTime.getTime()
    : null;
  const startTimeMs = session?.currentPhaseStartTime instanceof Date
    ? session.currentPhaseStartTime.getTime()
    : null;
  const phaseDuration = session?.currentPhaseDurationSeconds ?? null;
  const resetKey = options?.resetKey;
  const hasRemoteTimer = endTimeMs !== null || phaseDuration != null;
  const fallbackChanged = fallbackSeconds !== lastFallbackSecondsRef.current;
  const resetKeyChanged = resetKey !== lastResetKeyRef.current;
  const fallbackEndTimeMs = hasRemoteTimer
    ? null
    : (localEndTimeMsRef.current === null || fallbackChanged || resetKeyChanged)
      ? nowMs + fallbackSeconds * MS_IN_SECOND
      : localEndTimeMsRef.current;

  useEffect(() => {
    setNowMs(Date.now());

    const intervalId = window.setInterval(() => {
      setNowMs(Date.now());
    }, MS_IN_SECOND);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [endTimeMs, startTimeMs, phaseDuration, fallbackSeconds, resetKey]);

  useEffect(() => {
    if (hasRemoteTimer) {
      localEndTimeMsRef.current = null;
    } else {
      localEndTimeMsRef.current = fallbackEndTimeMs;
    }

    lastFallbackSecondsRef.current = fallbackSeconds;
    lastResetKeyRef.current = resetKey;
  }, [fallbackEndTimeMs, fallbackSeconds, hasRemoteTimer, resetKey]);

  if (endTimeMs !== null) {
    const diffMs = endTimeMs - nowMs;
    return ensurePositiveSeconds(diffMs / MS_IN_SECOND);
  }

  if (phaseDuration != null) {
    const actualStartTimeMs = startTimeMs ?? nowMs;
    const elapsedSeconds = (nowMs - actualStartTimeMs) / MS_IN_SECOND;
    const remainingSeconds = phaseDuration - elapsedSeconds;
    return ensurePositiveSeconds(remainingSeconds);
  }

  const diffMs = (fallbackEndTimeMs ?? nowMs) - nowMs;
  return ensurePositiveSeconds(diffMs / MS_IN_SECOND);
}

export default useSessionPhaseTimer;
